import axios, { AxiosInstance, AxiosError } from 'axios';
import { LoginResponse, User, UserUpdate } from '../types/auth';
import { decodeJWT } from '../utils/jwt';

const COPILOT_BASE_URL = process.env.REACT_APP_AUTH_URL || '';

// Singleton promise — prevents multiple simultaneous refresh calls
let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      // Hard timeout so a stalled refresh can't wedge the singleton forever.
      // Without this, every subsequent 401 await blocks on a dead promise,
      // freezing the entire app until the tab is closed.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      try {
        // /auth/auth-status is copilot's sliding-session refresher: called with
        // the session cookie, it re-mints a fresh access token when the current
        // one is within 5 min of expiry and returns it as `access_token`. This
        // is the same endpoint the copilot main app polls to stay logged in.
        // (The old POST /auth/refresh route does not exist on the server.)
        const res = await fetch(`${COPILOT_BASE_URL}/auth/auth-status`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) return null;
        const data = await res.json();
        const token = data?.access_token as string | undefined;
        if (token) {
          localStorage.setItem('auth_token', token);
          return token;
        }
        // No access_token in the body = the cookie token is still valid and not
        // yet near expiry (no refresh needed). Caller keeps the current token.
        return null;
      } catch {
        return null;
      } finally {
        clearTimeout(timeoutId);
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

class AuthApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${COPILOT_BASE_URL}/auth`,
      withCredentials: true,  // needed for session cookie refresh
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          const originalRequest: any = error.config;
          if (originalRequest && !originalRequest._retried) {
            originalRequest._retried = true;
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${newToken}`,
              };
              return this.client.request(originalRequest);
            }
          }
          this.removeToken();
          const publicPaths = ['/login', '/complete-registration'];
          if (!publicPaths.some(p => window.location.pathname.startsWith(p))) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null { return localStorage.getItem('auth_token'); }
  private setToken(token: string): void { localStorage.setItem('auth_token', token); }
  private removeToken(): void { localStorage.removeItem('auth_token'); }

  private async refreshToken(): Promise<string | null> {
    try {
      // Copilot's auth-status endpoint auto-refreshes via session cookie
      const res = await this.client.get('/auth-status');
      const token = res.data?.access_token;
      if (token) {
        this.setToken(token);
        return token;
      }
      return null;
    } catch {
      return null;
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    // Copilot expects OAuth2 form data at POST /auth/token
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await this.client.post<LoginResponse>('/token', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    this.setToken(response.data.access_token);
    return response.data;
  }

  getCurrentUser(): User | null {
    // User info is embedded in the JWT — no API call needed
    const token = this.getToken();
    if (!token) return null;
    const payload = decodeJWT(token);
    if (!payload?.user_info) return null;
    const u = payload.user_info;
    return {
      id: u.id,
      email: u.email,
      first_name: u.first_name,
      last_name: u.last_name,
      organization_id: u.organization_id,
      role_id: u.role_id,
      status: 'Active' as any,
      email_verified: true,
    };
  }

  async updateUser(userId: number, data: UserUpdate): Promise<User> {
    const response = await this.client.put<User>(`/users/${userId}`, data);
    return response.data;
  }

  logout(): void { this.removeToken(); }
  isAuthenticated(): boolean { return !!this.getToken(); }

  // ── Terms-of-service clickwrap (Copilot terms system, surface='notes') ──
  // Status/accept ride the same Copilot /auth client (Bearer token attached
  // by the request interceptor; Copilot accepts Bearer for these endpoints).

  async getTermsStatus(): Promise<{
    acceptance_required: boolean;
    current_version: string;
    terms_url: string;
    privacy_url: string;
    baa_url: string;
  }> {
    const res = await this.client.get('/terms-status');
    return res.data;
  }

  async acceptTerms(): Promise<boolean> {
    const res = await this.client.post('/accept-terms', { surface: 'notes' });
    return !!res.data?.accepted;
  }
}

export const authApi = new AuthApiClient();
